import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Package,
  ArrowLeft,
  Edit3,
  Save,
  X,
  DollarSign,
  AlertTriangle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const InventoryRow = ({ inv, onEdit, isEditing, onSave, onCancel }) => {
  const [draft, setDraft] = useState(inv);

  useEffect(() => setDraft(inv), [inv]);

  if (!isEditing) {
    const lowStock =
      inv.reorder_level !== null &&
      inv.current_quantity !== null &&
      inv.current_quantity <= inv.reorder_level;
    return (
      <tr
        data-testid={`inv-row-${inv.id}`}
        className="border-b border-stone-100 hover:bg-stone-50 group"
      >
        <td className="px-3 py-2.5">
          <div className="font-semibold text-stone-900">{inv.item_name}</div>
          <div className="text-[11px] text-stone-400">
            used in {inv.use_count} dish{inv.use_count === 1 ? "" : "es"}
          </div>
        </td>
        <td className="px-3 py-2.5 text-[13px] text-stone-600">
          <span className="font-medium">{inv.primary_unit}</span>
          {inv.units_available.length > 1 && (
            <span className="text-stone-400">
              {" "}
              · {inv.units_available.filter((u) => u !== inv.primary_unit).join(", ")}
            </span>
          )}
        </td>
        <td className="px-3 py-2.5 text-[13px] text-stone-700 tabular-nums">
          {inv.unit_cost ? `$${Number(inv.unit_cost).toFixed(2)}` : <span className="text-stone-300">—</span>}
        </td>
        <td className="px-3 py-2.5 text-[13px] text-stone-700 tabular-nums">
          <span className={lowStock ? "text-rose-600 font-bold" : ""}>
            {inv.current_quantity !== null ? inv.current_quantity : "—"}
          </span>
          {lowStock && <AlertTriangle className="w-3.5 h-3.5 inline ml-1 text-rose-500" />}
        </td>
        <td className="px-3 py-2.5 text-[13px] text-stone-600">
          {inv.supplier_name || <span className="text-stone-300">—</span>}
        </td>
        <td className="px-3 py-2.5 text-[12px] text-stone-500 font-mono">
          {inv.supplier_code || "—"}
        </td>
        <td className="px-3 py-2.5 text-right">
          <button
            data-testid={`inv-edit-${inv.id}`}
            onClick={() => onEdit(inv.id)}
            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-stone-200 text-stone-500 transition-opacity"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b-2 border-stone-300 bg-stone-50">
      <td className="px-3 py-2.5">
        <div className="font-semibold text-stone-900">{inv.item_name}</div>
        <div className="text-[11px] text-stone-400">used in {inv.use_count} dishes</div>
      </td>
      <td className="px-3 py-2.5">
        <select
          value={draft.primary_unit}
          onChange={(e) => setDraft({ ...draft, primary_unit: e.target.value })}
          className="px-2 py-1 rounded-md border border-stone-300 text-[13px] bg-white w-24"
        >
          {[...new Set([...draft.units_available, "each", "g", "kg", "ml", "L"])].map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2.5">
        <input
          type="number"
          step="0.01"
          value={draft.unit_cost ?? ""}
          onChange={(e) => setDraft({ ...draft, unit_cost: e.target.value === "" ? null : Number(e.target.value) })}
          placeholder="0.00"
          className="w-20 px-2 py-1 rounded-md border border-stone-300 text-[13px] tabular-nums"
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          type="number"
          value={draft.current_quantity ?? ""}
          onChange={(e) => setDraft({ ...draft, current_quantity: e.target.value === "" ? null : Number(e.target.value) })}
          placeholder="0"
          className="w-20 px-2 py-1 rounded-md border border-stone-300 text-[13px] tabular-nums"
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          value={draft.supplier_name ?? ""}
          onChange={(e) => setDraft({ ...draft, supplier_name: e.target.value || null })}
          placeholder="Supplier"
          className="w-32 px-2 py-1 rounded-md border border-stone-300 text-[13px]"
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          value={draft.supplier_code ?? ""}
          onChange={(e) => setDraft({ ...draft, supplier_code: e.target.value || null })}
          placeholder="SKU"
          className="w-24 px-2 py-1 rounded-md border border-stone-300 text-[12px] font-mono"
        />
      </td>
      <td className="px-3 py-2.5 text-right">
        <button
          data-testid={`inv-save-${inv.id}`}
          onClick={() => onSave(inv.id, draft)}
          className="p-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 mr-1"
        >
          <Save className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCancel}
          className="p-1.5 rounded-lg hover:bg-stone-200 text-stone-500"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
};

export default function AdminInventoryPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("use_count");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/api/directory/admin/inventory`, window.location.origin);
      url.searchParams.set("limit", "300");
      url.searchParams.set("sort", sort);
      if (q) url.searchParams.set("q", q);
      const r = await fetch(url.toString());
      const data = await r.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sort]);

  const save = async (id, draft) => {
    const update = {};
    [
      "unit_cost",
      "current_quantity",
      "reorder_level",
      "supplier_name",
      "supplier_code",
      "last_purchase_invoice",
      "last_purchase_date",
      "last_purchase_qty",
      "pack_outer",
      "pack_inner",
      "pack_unit",
    ].forEach((k) => {
      if (draft[k] !== undefined) update[k] = draft[k];
    });
    const r = await fetch(`${API}/api/directory/admin/inventory/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(update),
    });
    if (r.ok) {
      setItems((is) => is.map((i) => (i.id === id ? { ...i, ...update } : i)));
      setEditingId(null);
    }
  };

  const stats = useMemo(
    () => ({
      total,
      withCost: items.filter((i) => i.unit_cost).length,
      withSupplier: items.filter((i) => i.supplier_name).length,
      lowStock: items.filter(
        (i) =>
          i.reorder_level !== null &&
          i.current_quantity !== null &&
          i.current_quantity <= i.reorder_level,
      ).length,
    }),
    [items, total],
  );

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/admin/bookings"
            className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <Package className="w-5 h-5 text-stone-600" />
          <h1 className="font-serif text-xl text-stone-900 tracking-tight">
            Inventory · ingredients
          </h1>
          <span className="ml-auto text-[13px] text-stone-500">
            <span className="font-semibold text-stone-900">{total}</span> items · auto-extracted from menus
          </span>
        </div>
        <div className="border-t border-stone-100">
          <div className="max-w-7xl mx-auto px-4 py-3 flex gap-3 flex-wrap items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
              <input
                data-testid="inventory-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search ingredient name…"
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-stone-200 text-[13px] focus:outline-none focus:border-stone-400"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white border border-stone-200 text-[13px]"
            >
              <option value="use_count">Sort: Most used</option>
              <option value="name">Sort: A → Z</option>
            </select>
            <div className="flex gap-2 ml-auto text-[12px]">
              <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                <DollarSign className="w-3 h-3 inline" /> {stats.withCost} priced
              </span>
              <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                {stats.withSupplier} with supplier
              </span>
              {stats.lowStock > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 font-medium">
                  <AlertTriangle className="w-3 h-3 inline" /> {stats.lowStock} low stock
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {error && (
          <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 text-[13px] rounded-lg">
            {error}
          </div>
        )}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
              <tr>
                <th className="px-3 py-2.5">Ingredient</th>
                <th className="px-3 py-2.5">Units</th>
                <th className="px-3 py-2.5">Cost</th>
                <th className="px-3 py-2.5">Qty on hand</th>
                <th className="px-3 py-2.5">Supplier</th>
                <th className="px-3 py-2.5">SKU</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-stone-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-stone-400">No ingredients found</td></tr>
              ) : (
                items.map((inv) => (
                  <InventoryRow
                    key={inv.id}
                    inv={inv}
                    isEditing={editingId === inv.id}
                    onEdit={setEditingId}
                    onSave={save}
                    onCancel={() => setEditingId(null)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-stone-400 text-center mt-6">
          Tap a row to fill in cost, supplier and SKU · Stock data will flow into your master DB
        </p>
      </div>
    </div>
  );
}
